import React from 'react'
import { Icon as LegacyIcon } from '@ant-design/compatible'
import { Link } from 'react-router-dom'
import pathToRegexp from 'path-to-regexp'
import {
  memoize,
  reduce,
  groupBy,
  filter,
  entries,
  compose,
  map
} from 'lodash/fp'
import produce from 'immer'
import SCENSE_ENUM from '@enum/workbenchScenses'
import { traverseTreeCreator } from './utils'

// 转化路径
const conversionPath = path => {
  if (path && path.indexOf('http') === 0) {
    return path
  } else {
    return `/${path || ''}`.replace(/\/+/g, '/')
  }
}

/**
 * 判断是否是http链接.返回 Link 或 a
 * Judge whether it is http link.return a or Link
 * @memberof SiderMenu
 */
const getMenuItemPath = (item, callback) => {
  const itemPath = conversionPath(item.path)
  const { target, name, icon } = item
  // Is it a http link
  if (/^https?:\/\//.test(itemPath)) {
    return (
      <a href={itemPath} target={target}>
        <LegacyIcon type={icon} />
        <span>{name}</span>
      </a>
    )
  }
  return (
    <Link
      to={itemPath}
      target={target}
      onClick={() => {
        if (callback) {
          callback()
        }
      }}
    >
      <LegacyIcon type={icon} />
      <span>{name}</span>
    </Link>
  )
}

const urlToList = url => {
  const urllist = url.split('/').filter(i => i)
  return urllist.map((urlItem, index) => {
    return `/${urllist.slice(0, index + 1).join('/')}`
  })
}

const getMenuTopFistUrl = item => {
  if (item.children && item.children.some(child => child.name)) {
    item.children[0].name = item.name
    return getMenuTopFistUrl(item.children[0])
  } else {
    return item
  }
}

const getMenuTopFistUrlPath = (app, path = '') => {
  if (app?.menu?.length) {
    let screen = app.menu.filter(m => m.views && m.views.length)[0]
    if (screen?.children?.length) {
      screen = screen.children.filter(m => m.views && m.views.length)[0]
    }
    if (screen?.views?.length) {
      path = screen.views[0].url.replace('..', '')
    }
  }
  return path
}

/**
 * Recursively flatten the data
 * [{path:string},{path:string}] => {path,path2}
 * @param  menu
 */
const getFlatMenuKeys = menu =>
  menu.reduce((keys, item) => {
    keys.push(item.path)
    if (item.children) {
      return keys.concat(getFlatMenuKeys(item.children))
    }
    return keys
  }, [])

const getMenuMatchKeys = (flatMenuKeys, paths) => {
  return flatMenuKeys.filter(item =>
    pathToRegexp(item.path).test(paths[paths.length - 1])
  )
}

const getDefaultCollapsedSubMenus = (menus, pathname) => {
  return getMenuMatchKeys(treeTransArray(menus), urlToList(pathname))
}

const formatter = data => {
  if (!data && !data.length) {
    return []
  }
  return data.map(item => {
    let obj = {}
    if (item.children) {
      obj.id = item.id
      obj.name = item.name || item.displayName
      obj.children = formatter(item.children)
    } else if (item.views && item.views.length) {
      if (item.views.length === 1) {
        obj = {
          id: item.id,
          name: item.displayName,
          path: item.views[0].url.replace('..', '')
        }
      } else {
        obj.id = item.id
        obj.name = item.name || item.displayName
        obj.children = formatter(item.views)
      }
    } else {
      obj = {
        id: item.id,
        name: item.name,
        path: item.url ? item.url.replace('..', '') : ''
      }
    }
    return obj
  })
}

const findMenu = (parent, item = { ids: [], names: [] }) => {
  if (!parent) {
    return item
  }
  if (parent.id || parent.name) {
    item.ids.push(parent.id + '')
    item.names.push({
      name: parent.name,
      path: parent.path || ''
    })
  }
  findMenu(parent.parent, item)
  return item
}

const bindParents = produce(
  traverseTreeCreator((node, _, parent) => {
    node.parent = parent
  })
)
const treeTransArray = memoize(data => {
  let realData = []
  if (!data && !data.length) {
    return realData
  }

  let index = 0
  compose(
    traverseTreeCreator(node => {
      index += 1
      if (node.path) {
        let parent =
          index > 1
            ? findMenu(node)
            : {
                ids: [node.id + ''],
                names: [{ name: node.name, path: node.path }]
              }
        let item = {
          id: `${node.id}`,
          path: node.path,
          name: node.name,
          parentIds: parent.ids.reverse(),
          parentNames: parent.names.reverse()
        }
        realData.push(item)
      }
    }),
    bindParents,
    formatter
  )(data)

  return realData
})

const parseAppMenuData = appData => {
  const flatten = (selector = i => i) =>
    reduce(
      (result, item) =>
        Array.isArray(selector(item))
          ? [...result, ...flatten(selector)(selector(item))]
          : [...result, item],
      []
    )
  const selector = i => {
    return i.children || i.views
  }

  return compose(
    map(i => ({
      ...i,
      applicationCode: appData.code
    })),
    flatten(selector)
  )(appData.menu)
}

const createParseAppMenuDataFn = (...fns) =>
  memoize(
    compose(
      ...fns,
      reduce((result, menu) => [...result, ...menu], []),
      map(parseAppMenuData)
    )
  )

const getWorkbenchMenus = createParseAppMenuDataFn(
  map(([id, children]) => ({
    id,
    displayName: SCENSE_ENUM[id],
    children: map(i => ({
      ...i,
      name: i.cnDisplayname || i.name
    }))(children)
  })),
  entries,
  groupBy(i => i.accessDataType),
  filter(i => i.accessDataType)
)

const getAppMenuApplication = createParseAppMenuDataFn(
  reduce(
    (result, item) => ({
      ...result,
      [item.url.replace('..', '')]: item.applicationCode
    }),
    {}
  )
)

const getAppMenuConfig = createParseAppMenuDataFn(
  reduce(
    (result, item) => ({
      ...result,
      [item.url.replace('..', '')]: item
    }),
    {}
  )
)

/**
 * @param {Array} menuData
 * @param {String} applicationCode
 */
const applicationSelector = (menuData = [], applicationCode) => {
  const defaultApplication = menuData[0]
  if (!applicationCode) return defaultApplication
  const result = menuData.filter(i => i.code === applicationCode)
  return result.length ? result[0] : defaultApplication
}

export {
  conversionPath,
  getMenuItemPath,
  getFlatMenuKeys,
  urlToList,
  getMenuMatchKeys,
  getMenuTopFistUrl,
  getDefaultCollapsedSubMenus,
  formatter,
  getMenuTopFistUrlPath,
  treeTransArray,
  getAppMenuApplication,
  getAppMenuConfig,
  getWorkbenchMenus,
  applicationSelector
}
